import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../components/ui/tabs";
import ServiceOrderList from "./components/ServiceOrderList";
import CustomerLookup from "./components/CustomerLookup";
import CreateServiceOrder from "./components/CreateServiceOrder";
import CustomerProfiling from "./components/CustomerProfiling";
import ServiceMap from "./components/ServiceMap";
import FieldEngineers from "./components/FieldEngineers";
import { normalizeServiceOrderStatus } from "./serviceOrderStatus";
import {
  ClipboardList,
  MapPin,
  TrendingUp,
  AlertCircle,
  CheckCircle,
  Clock,
} from "lucide-react";

const SERVICE_ORDERS_PAGE_SIZE = 200;

const FiledUtilityDashboard: React.FC = () => {
  const [serviceOrders, setServiceOrders] = useState<Array<{ status: string }>>(
    [],
  );
  const [serviceOrdersTotal, setServiceOrdersTotal] = useState<number>(0);
  const [techniciansAvailable, setTechniciansAvailable] = useState<number>(0);
  const [overviewLoading, setOverviewLoading] = useState<boolean>(false);

  const fetchAllServiceOrdersForDashboard = useCallback(async (): Promise<{
    orders: Array<{ status: string }>;
    total: number;
  }> => {
    let page = 1;
    let totalFromApi = 0;
    const orders: Array<{ status: string }> = [];

    for (;;) {
      const response = await fetch(
        `https://backend.voltreum.com/api/v2/service-orders?page=${page}&pageSize=${SERVICE_ORDERS_PAGE_SIZE}`,
      );
      if (!response.ok) {
        throw new Error(`Service orders request failed: ${response.status}`);
      }
      const result = await response.json();
      const payload = result?.data;
      const batch = Array.isArray(payload?.data) ? payload.data : [];
      if (typeof payload?.total === "number") {
        totalFromApi = payload.total;
      }
      for (const row of batch) {
        const status =
          typeof row === "object" && row !== null && "status" in row
            ? String((row as { status?: string }).status || "")
            : "";
        orders.push({ status });
      }
      if (batch.length === 0) break;
      if (totalFromApi > 0 && orders.length >= totalFromApi) break;
      if (batch.length < SERVICE_ORDERS_PAGE_SIZE) break;
      page += 1;
      if (page > 500) break;
    }

    return {
      orders,
      total: totalFromApi > 0 ? totalFromApi : orders.length,
    };
  }, []);

  const refreshOverview = useCallback(async (): Promise<void> => {
    setOverviewLoading(true);
    try {
      const [ordersSummary, engineersResponse] = await Promise.all([
        fetchAllServiceOrdersForDashboard(),
        fetch(
          "https://backend.voltreum.com/api/v2/field-engineers?activeOnly=true",
        ),
      ]);

      setServiceOrders(ordersSummary.orders);
      setServiceOrdersTotal(ordersSummary.total);

      const engineersResult = await engineersResponse.json();
      const engineersData = engineersResult?.data;
      const activeEngineers = Array.isArray(engineersData) ? engineersData : [];
      setTechniciansAvailable(activeEngineers.length);
    } catch (error) {
      console.error("Failed to fetch dashboard counts:", error);
      setServiceOrders([]);
      setServiceOrdersTotal(0);
      setTechniciansAvailable(0);
    } finally {
      setOverviewLoading(false);
    }
  }, [fetchAllServiceOrdersForDashboard]);

  useEffect(() => {
    void refreshOverview();
  }, [refreshOverview]);

  const normalizedStatuses = useMemo(
    () =>
      serviceOrders.map((order) =>
        normalizeServiceOrderStatus(order.status || ""),
      ),
    [serviceOrders],
  );

  const totalOrders =
    serviceOrdersTotal > 0 ? serviceOrdersTotal : serviceOrders.length;
  const openOrders = normalizedStatuses.filter(
    (status) =>
      status === "pending" || status === "assigned" || status === "in_progress",
  ).length;
  const completedOrders = normalizedStatuses.filter(
    (status) => status === "completed",
  ).length;

  const getPriorityColor = (priority: string): string => {
    switch (priority) {
      case "urgent":
        return "bg-red-100 text-red-800";
      case "high":
        return "bg-orange-100 text-orange-800";
      case "medium":
        return "bg-yellow-100 text-yellow-800";
      case "low":
        return "bg-green-100 text-green-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  return (
    <div className="space-y-6">
      {/* <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-end">
        <Button
          type="button"
          variant="outline"
          onClick={() => void refreshOverview()}
          disabled={overviewLoading}
          className="shrink-0 self-start sm:self-auto"
        >
          {overviewLoading ? "Loading…" : "Refresh overview"}
        </Button>
      </div> */}

      {/* Dashboard Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Total Service Orders</CardTitle>
            <CardDescription>
              All service requests in the system
            </CardDescription>
          </CardHeader>
          <CardContent className="flex items-center justify-between">
            <div className="text-2xl font-bold">{totalOrders}</div>
            <TrendingUp className="h-6 w-6 text-green-500" />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Open Service Orders</CardTitle>
            <CardDescription>
              Service requests pending completion
            </CardDescription>
          </CardHeader>
          <CardContent className="flex items-center justify-between">
            <div className="text-2xl font-bold">{openOrders}</div>
            <AlertCircle className="h-6 w-6 text-orange-500" />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Completed Service Orders</CardTitle>
            <CardDescription>
              Service requests successfully completed
            </CardDescription>
          </CardHeader>
          <CardContent className="flex items-center justify-between">
            <div className="text-2xl font-bold">{completedOrders}</div>
            <CheckCircle className="h-6 w-6 text-blue-500" />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Technicians Available</CardTitle>
            <CardDescription>Field agents ready for assignment</CardDescription>
          </CardHeader>
          <CardContent className="flex items-center justify-between">
            <div className="text-2xl font-bold">{techniciansAvailable}</div>
            <Clock className="h-6 w-6 text-purple-500" />
          </CardContent>
        </Card>
      </div>

      {/* Main Content Tabs */}
      <Tabs defaultValue="orders" className="space-y-4">
        <TabsList className="grid w-full grid-cols-4 bg-gray-100">
          <TabsTrigger value="orders" className="flex items-center gap-2">
            <ClipboardList className="h-4 w-4" />
            Service Orders
          </TabsTrigger>
          {/* <TabsTrigger value="customers" className="flex items-center gap-2">
            <Users className="h-4 w-4" />
            Customer Lookup
          </TabsTrigger>
          <TabsTrigger value="profiling" className="flex items-center gap-2">
            <User className="h-4 w-4" />
            Customer Profiling
          </TabsTrigger> */}
          <TabsTrigger value="create" className="flex items-center gap-2">
            <ClipboardList className="h-4 w-4" />
            Create Order
          </TabsTrigger>
          <TabsTrigger
            value="field-engineers"
            className="flex items-center gap-2"
          >
            <ClipboardList className="h-4 w-4" />
            Field Engineers
          </TabsTrigger>
          <TabsTrigger value="map" className="flex items-center gap-2">
            <MapPin className="h-4 w-4" />
            Service Map
          </TabsTrigger>
          {/* <TabsTrigger value="analytics" className="flex items-center gap-2">
            <TrendingUp className="h-4 w-4" />
            Analytics
          </TabsTrigger> */}
        </TabsList>

        <TabsContent value="orders" forceMount>
          <ServiceOrderList />
        </TabsContent>

        <TabsContent value="customers" forceMount>
          <CustomerLookup />
        </TabsContent>

        <TabsContent value="profiling" forceMount>
          <CustomerProfiling />
        </TabsContent>

        <TabsContent value="create" forceMount>
          <CreateServiceOrder />
        </TabsContent>

        <TabsContent value="field-engineers" forceMount>
          <FieldEngineers />
        </TabsContent>

        <TabsContent value="map" forceMount>
          <ServiceMap />
        </TabsContent>

        <TabsContent value="analytics" forceMount>
          <Card>
            <CardHeader>
              <CardTitle>Service Analytics</CardTitle>
              <CardDescription>Performance metrics and trends</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-center py-8 text-gray-500">
                <TrendingUp className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>Analytics dashboard coming soon...</p>
                <p className="text-sm">
                  Track performance metrics, completion rates, and customer
                  satisfaction
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default FiledUtilityDashboard;
